import { useCallback, useEffect, useState } from 'react';

export const COLOR_THEMES = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
} as const);

export type ColorTheme = (typeof COLOR_THEMES)[keyof typeof COLOR_THEMES];

export const COLOR_THEME_STORAGE_KEY = 'showroom-color-theme';

const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';
const THEME_COLORS: Readonly<Record<ColorTheme, string>> = Object.freeze({
  [COLOR_THEMES.LIGHT]: '#f0f0f3',
  [COLOR_THEMES.DARK]: '#0f0f10',
});

/**
 * localStorage와 data 속성에서 읽은 값은 무엇이든 들어올 수 있어서 unknown으로 받습니다.
 */
function isColorTheme(value: unknown): value is ColorTheme {
  return value === COLOR_THEMES.LIGHT || value === COLOR_THEMES.DARK;
}

function readStoredColorTheme(): ColorTheme | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedTheme = window.localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    return isColorTheme(storedTheme) ? storedTheme : null;
  } catch {
    return null;
  }
}

function resolveSystemColorTheme(): ColorTheme {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia(DARK_MODE_QUERY).matches ? COLOR_THEMES.DARK : COLOR_THEMES.LIGHT;
  }

  return COLOR_THEMES.LIGHT;
}

function resolveInitialColorTheme(): ColorTheme {
  if (typeof document !== 'undefined') {
    const bootstrappedTheme = document.documentElement.dataset.theme;
    if (isColorTheme(bootstrappedTheme)) {
      return bootstrappedTheme;
    }
  }

  return readStoredColorTheme() ?? resolveSystemColorTheme();
}

function applyColorTheme(theme: ColorTheme): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', THEME_COLORS[theme]);
  }
}

export interface ColorThemeController {
  theme: ColorTheme;
  setTheme: (nextTheme: ColorTheme) => void;
}

export function useColorTheme(): ColorThemeController {
  const [theme, setThemeState] = useState<ColorTheme>(resolveInitialColorTheme);

  useEffect(() => {
    applyColorTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const colorSchemeQuery = window.matchMedia(DARK_MODE_QUERY);
    const handleSystemThemeChange = (event: MediaQueryListEvent): void => {
      // 사용자가 한 번이라도 직접 고른 뒤에는 OS 변경보다 저장된 선택을 우선합니다.
      if (readStoredColorTheme() === null) {
        setThemeState(event.matches ? COLOR_THEMES.DARK : COLOR_THEMES.LIGHT);
      }
    };

    if (typeof colorSchemeQuery.addEventListener === 'function') {
      colorSchemeQuery.addEventListener('change', handleSystemThemeChange);
      return () => colorSchemeQuery.removeEventListener('change', handleSystemThemeChange);
    }

    // Safari 13 이하의 구형 MediaQueryList API fallback입니다.
    colorSchemeQuery.addListener(handleSystemThemeChange);
    return () => colorSchemeQuery.removeListener(handleSystemThemeChange);
  }, []);

  const setTheme = useCallback((nextTheme: ColorTheme) => {
    if (!isColorTheme(nextTheme)) {
      return;
    }

    try {
      window.localStorage.setItem(COLOR_THEME_STORAGE_KEY, nextTheme);
    } catch {
      // 저장이 차단된 환경에서도 현재 세션의 테마 전환은 정상 동작해야 합니다.
    }

    setThemeState(nextTheme);
  }, []);

  return { theme, setTheme };
}
